import React, { useState, useEffect } from "react";
import { Search, ChevronDown, Filter } from "lucide-react";
import TradeTable from "./TradeTable";
import TradeFilters from "./TradeFilters";
import { getUserTrades } from "../../utils/firebaseTradeUtils";
import { useAuth } from "../../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

const TradeLog = () => {
  const [trades, setTrades] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: "all",
    symbol: "",
    direction: "all",
    status: "all",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [userSettings, setUserSettings] = useState({
    useCommissions: false,
    commissionValue: 0,
  });
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        const fetchedTrades = await getUserTrades(user.uid);
        const settingsRef = doc(db, "userSettings", user.uid);
        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists()) {
          setUserSettings(settingsSnap.data());
        }
        setTrades(fetchedTrades);
      } catch (error) {
        console.error("Error fetching trades:", error);
      }
    };

    fetchData();

    const handleTradeUpdate = () => {
      fetchData();
    };

    window.addEventListener("tradesUpdated", handleTradeUpdate);
    window.addEventListener("storage", handleTradeUpdate);

    return () => {
      window.removeEventListener("tradesUpdated", handleTradeUpdate);
      window.removeEventListener("storage", handleTradeUpdate);
    };
  }, [user]);

  const getFilteredTrades = () => {
    return trades.filter((trade) => {
      // Date range filter
      if (filters.dateRange !== "all") {
        const tradeDate = new Date(trade.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (filters.dateRange) {
          case "today":
            if (tradeDate.toDateString() !== today.toDateString()) return false;
            break;
          case "week": {
            const weekAgo = new Date(today.setDate(today.getDate() - 7));
            if (tradeDate < weekAgo) return false;
            break;
          }
          case "month": {
            const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
            if (tradeDate < monthAgo) return false;
            break;
          }
          default:
            break;
        }
      }

      // Symbol filter
      if (
        filters.symbol &&
        !trade.symbol.includes(filters.symbol.toUpperCase())
      ) {
        return false;
      }

      // Direction filter
      if (
        filters.direction !== "all" &&
        trade.direction !== filters.direction
      ) {
        return false;
      }

      // Status filter
      if (filters.status !== "all" && trade.status !== filters.status) {
        return false;
      }

      // Search query for symbol, setup, and tags
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSymbol = trade.symbol.toLowerCase().includes(query);
        const matchesSetup = trade.setup?.toLowerCase().includes(query);
        const matchesTags = trade.tags?.some((tag) =>
          tag.text.toLowerCase().includes(query)
        );

        return matchesSymbol || matchesSetup || matchesTags;
      }

      return true;
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Get filtered and sorted trades
  const filteredTrades = getFilteredTrades().sort((a, b) => {
    if (sortConfig.key === "date") {
      return sortConfig.direction === "asc"
        ? new Date(a.date) - new Date(b.date)
        : new Date(b.date) - new Date(a.date);
    }

    if (sortConfig.direction === "asc") {
      return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
    }
    return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
  });

  return (
    <div className="trade-log">
      <div className="trade-log-header">
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by symbol, setup, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={20} />
          <span>Filters</span>
          <ChevronDown
            size={16}
            className={`transition-transform ${
              showFilters ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {showFilters && (
        <TradeFilters filters={filters} onFilterChange={setFilters} />
      )}

      <TradeTable
        trades={filteredTrades}
        sortConfig={sortConfig}
        onSort={handleSort}
        userSettings={userSettings}
      />

      {trades.length === 0 && (
        <div className="no-trades-message">
          <p>No trades found. Import trades to get started.</p>
        </div>
      )}
    </div>
  );
};

export default TradeLog;
